/**
 * GridTable.jsx — the 9-cell eye-test grid table.
 *
 * Extracted verbatim from RenderingStudioLegacyGrid.jsx (§6.6 A1).
 * Renders one row per CharSpeechSample cell with live status, the
 * per-cell audio player, and render meta. Highlights the cell the
 * in-flight job is currently rendering (`job.currentCellId`). Surfaces
 * `cell.reason` / `cell.error` (e.g. skipped-because-no-active-clone).
 */

import React from "react";
import { Table, Label } from "semantic-ui-react";
import CellAudioPlayer from "./CellAudioPlayer";
import { cellStatusClass, formatDuration, effectiveText } from "./gridUtils";


const GridTable = ({ grid, job }) => {
  if (!grid) return null;
  const cells = Array.isArray(grid.cells) ? grid.cells : [];
  if (cells.length === 0) {
    return (
      <div className="empty-hint">
        This character has no CharSpeechSamples yet.
      </div>
    );
  }
  const activeCellId = job?.currentCellId || null;
  return (
    <Table compact celled className="rs-grid-table">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Sample</Table.HeaderCell>
          <Table.HeaderCell>Emotion</Table.HeaderCell>
          <Table.HeaderCell>Text</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Audio</Table.HeaderCell>
          <Table.HeaderCell>Meta</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {cells.map((c) => {
          const status = c.status || (c.lastRendered ? "idle" : "idle");
          const isActive =
            activeCellId &&
            (activeCellId === c.characterSampleId ||
              activeCellId === c.lastRendered?.cellId);
          const reason = c.reason || c.error || null;
          const {
            text: cellText,
            isDefault: textIsDefault,
            isMissing: textIsMissing,
          } = effectiveText(c);

          const cloneReady = c.cloneIsActive && c.cloneStatus === "success";

          return (
            <Table.Row
              key={c.characterSampleId}
              className={`grid-row${isActive ? " is-active" : ""}`}
            >
              <Table.Cell>
                <strong>{c.sampleName || c.characterSampleId}</strong>
              </Table.Cell>
              <Table.Cell>
                {c.emotionCategory || "—"}
                {c.intensity ? (
                  <Label size="mini" style={{ marginLeft: 4 }}>
                    {c.intensity}
                  </Label>
                ) : null}
              </Table.Cell>
              <Table.Cell>
                {textIsMissing ? (
                  <div
                    className="grid-text grid-text--missing"
                    style={{ color: "#999", fontStyle: "italic" }}
                  >
                    {cellText}
                  </div>
                ) : (
                  <div className="grid-text">{cellText}</div>
                )}
                {textIsDefault && (
                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: "#999",
                      fontStyle: "italic",
                      marginTop: 2,
                    }}
                  >
                    (default line — not book-derived)
                  </div>
                )}
              </Table.Cell>


              <Table.Cell>
                <span className={`grid-cell-status ${cellStatusClass(status)}`}>
                  {status}
                </span>
                {reason && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#8a5a00",
                      marginTop: 4,
                    }}
                  >
                    {reason}
                  </div>
                )}
              </Table.Cell>
              <Table.Cell>
                <CellAudioPlayer cell={c} />
              </Table.Cell>
              <Table.Cell>
                <div style={{ fontSize: "0.75rem", color: "#666" }}>
                  {c.lastRendered?.durationMs && (
                    <div>dur {formatDuration(c.lastRendered.durationMs)}</div>
                  )}
                  {c.lastRendered?.updatedAt && (
                    <div>
                      {new Date(c.lastRendered.updatedAt).toLocaleString()}
                    </div>
                  )}
                  {!c.cloneIsActive ? (
                    <div style={{ color: "#b8362e" }}>no active clone</div>
                  ) : (
                    !c.lastRendered && (
                      <div style={{ color: "#1a7f37" }}>
                        {cloneReady ? "clone ready" : `clone: ${c.cloneStatus || "?"}`}
                      </div>
                    )
                  )}

                </div>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

export default GridTable;
